/**
 * Opt-in tracing for the saved column layout (`display_columns_*`).
 *
 * Turn on in the console, reload, reproduce, read the log:
 *
 *   localStorage.debugColumns = '1'
 *
 * Off by default and installed only when the flag is set, so it costs nothing
 * for everyone else. Turn off with `localStorage.removeItem('debugColumns')`.
 *
 * It patches `localStorage.setItem` / `removeItem` rather than instrumenting the
 * known call sites, because the whole difficulty with this bug has been that
 * SOMETHING overwrites the layout and the interesting question is *who* — a
 * stack trace at the moment of the write answers that, including from code
 * nobody thought to look at.
 */
const KEY_PATTERN = /display_columns/i

export const columnTraceEnabled = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('debugColumns') === '1'

/** Field order of a stored value, for whichever user it belongs to. */
const describe = (value) => {
  try {
    const parsed = JSON.parse(value)
    const perUser = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? Object.values(parsed)[0]
      : parsed

    return Array.isArray(perUser)
      ? perUser.map(col => col?.field).join(' ')
      : String(value).slice(0, 120)
  } catch (error) {
    return String(value).slice(0, 120)
  }
}

export const traceColumns = (stage, fields) => {
  if (!columnTraceEnabled()) return

  console.log(
    `%c[cols] ${stage}`,
    'color:#0086FF;font-weight:bold',
    Array.isArray(fields) ? fields.join(' ') : fields,
  )
}

let installed = false

export const installColumnTrace = () => {
  if (installed || !columnTraceEnabled()) return
  installed = true

  const { setItem, removeItem } = window.localStorage
  const stack = () => (new Error().stack || '').split('\n').slice(3, 7).join('\n')

  window.localStorage.setItem = function (key, value) {
    if (KEY_PATTERN.test(key)) {
      console.log(`%c[cols] WRITE ${key}`, 'color:#0E9F6E;font-weight:bold', describe(value), '\n', stack())
    }
    return setItem.call(this, key, value)
  }

  window.localStorage.removeItem = function (key) {
    if (KEY_PATTERN.test(key)) {
      console.log(`%c[cols] DELETE ${key}`, 'color:#F05252;font-weight:bold', '\n', stack())
    }
    return removeItem.call(this, key)
  }

  console.log('%c[cols] tracing on — reload, reorder, reload, then copy this log', 'color:#0086FF')
}
